import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

// Icons
import {
  FileText,
  Video,
  CheckCircle2,
  Clock,
  Sparkles,
  Play,
  Calendar,
  TrendingUp,
  Wand2,
  ChevronRight,
  User,
  Loader2
} from "lucide-react";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentScripts, setRecentScripts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, scriptsRes, profileRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/videos?limit=5'),
        api.get('/abraham/profile')
      ]);
      
      setStats(statsRes.data);
      setRecentScripts(scriptsRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "status-badge status-draft",
      approved: "status-badge status-approved",
      generating: "status-badge status-generating",
      completed: "status-badge status-completed"
    };
    return <span className={styles[status] || styles.draft}>{status}</span>;
  };

  const progressPercentage = stats ? Math.round((stats.completed_videos / 365) * 100) : 0;

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 lg:col-span-9">
            <Skeleton className="h-[300px] rounded-xl" />
          </div>
          <div className="md:col-span-4 lg:col-span-3 space-y-6">
            <Skeleton className="h-[140px] rounded-xl" />
            <Skeleton className="h-[140px] rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">
            Shalom, Creator
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage Abraham's 365-day journey through the ages
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/scripts">
            <Button className="btn-secondary gap-2" data-testid="view-all-scripts-btn">
              <FileText className="w-4 h-4" />
              View Scripts
            </Button>
          </Link>
          <Link to="/generate">
            <Button className="btn-primary gap-2" data-testid="generate-content-btn">
              <Wand2 className="w-4 h-4" />
              Generate
            </Button>
          </Link>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Hero Card - Progress Overview */}
        <Card className="card-patriarch md:col-span-8 lg:col-span-9 row-span-2 overflow-hidden" data-testid="progress-card">
          <div className="relative h-full min-h-[300px]">
            {/* Background Image */}
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1771325676184-44d8035e3cd1?crop=entropy&cs=srgb&fm=jpg&q=85')",
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            />
            <div className="relative z-10 p-6 md:p-8 h-full flex flex-col">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-sm font-accent tracking-widest text-muted-foreground uppercase mb-2">
                    The Journey
                  </p>
                  <h2 className="text-3xl md:text-4xl font-display font-bold">
                    365 Days of Abraham
                  </h2>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-medium">{progressPercentage}% Complete</span>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
                  <div className="text-center md:text-left">
                    <p className="text-3xl md:text-4xl font-display font-bold text-foreground">
                      {stats?.total_scripts || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Scripts</p>
                  </div>
                  <div className="text-center md:text-left">
                    <p className="text-3xl md:text-4xl font-display font-bold text-green-600 dark:text-green-400">
                      {stats?.completed_videos || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Completed</p>
                  </div>
                  <div className="text-center md:text-left">
                    <p className="text-3xl md:text-4xl font-display font-bold text-accent">
                      {stats?.approved || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Approved</p>
                  </div>
                  <div className="text-center md:text-left">
                    <p className="text-3xl md:text-4xl font-display font-bold text-muted-foreground">
                      {stats?.drafts || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Drafts</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Journey Progress</span>
                    <span className="font-medium">{stats?.completed_videos || 0} / 365 videos</span>
                  </div>
                  <Progress value={progressPercentage} className="h-3" />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Link to="/calendar">
                  <Button variant="outline" className="gap-2" data-testid="view-calendar-btn">
                    <Calendar className="w-4 h-4" />
                    View Calendar
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>

        {/* Profile Status Card */}
        <Card className="card-patriarch md:col-span-4 lg:col-span-3" data-testid="profile-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <User className="w-5 h-5 text-accent" />
              Abraham's Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {profile?.portrait_url ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${profile.portrait_url}`}
                  alt="Abraham"
                  className="w-16 h-16 rounded-full object-cover avatar-patriarch"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <User className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1">
                <p className="font-display font-semibold">{profile?.name || "Abraham"}</p>
                <p className="text-sm text-muted-foreground">{profile?.title || "Father of Nations"}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-4 text-center">
              <div>
                <p className="font-display font-bold">{profile?.posts || 0}</p>
                <p className="text-xs text-muted-foreground">Posts</p>
              </div>
              <div>
                <p className="font-display font-bold">{profile?.followers?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Followers</p>
              </div>
              <div>
                <p className="font-display font-bold">{profile?.following || 3}</p>
                <p className="text-xs text-muted-foreground">Following</p>
              </div>
            </div>
            <Link to="/profile" className="mt-4 block">
              <Button variant="outline" className="w-full gap-2" size="sm" data-testid="view-profile-btn">
                View Profile
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Quick Actions Card */}
        <Card className="card-patriarch md:col-span-4 lg:col-span-3" data-testid="quick-actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/generate" className="block">
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3" data-testid="quick-generate-portrait">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-accent" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">Generate Portrait</p>
                  <p className="text-xs text-muted-foreground">AI-powered image</p>
                </div>
              </Button>
            </Link>
            <Link to="/scripts" className="block">
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3" data-testid="quick-generate-script">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">Generate Script</p>
                  <p className="text-xs text-muted-foreground">Create daily video</p>
                </div>
              </Button>
            </Link>
            <Link to="/generate" className="block">
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3" data-testid="quick-generate-voice">
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Play className="w-4 h-4 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">Generate Voice</p>
                  <p className="text-xs text-muted-foreground">TTS narration</p>
                </div>
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Scripts */}
        <Card className="card-patriarch md:col-span-8 lg:col-span-6" data-testid="recent-scripts-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-display flex items-center gap-2">
                <FileText className="w-5 h-5 text-accent" />
                Recent Scripts
              </CardTitle>
              <Link to="/scripts">
                <Button variant="ghost" size="sm" className="text-accent" data-testid="view-all-recent">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {recentScripts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No scripts yet</p>
                  <Link to="/scripts">
                    <Button variant="link" className="text-accent mt-2">
                      Generate your first script
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentScripts.map((script) => (
                    <Link 
                      key={script.id} 
                      to={`/scripts/${script.day_number}`}
                      className="block"
                    >
                      <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors group" data-testid={`script-item-${script.day_number}`}>
                        <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                          <span className="font-display font-bold text-accent">{script.day_number}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{script.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(script.status)}
                            {script.voice_url && (
                              <Badge variant="outline" className="text-xs">
                                <Play className="w-3 h-3 mr-1" />
                                Voice
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Next Scheduled */}
        <Card className="card-patriarch md:col-span-4 lg:col-span-3" data-testid="next-scheduled-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              Next Scheduled
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.next_scheduled ? (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                  <p className="text-sm text-muted-foreground">Day {stats.next_scheduled.day_number}</p>
                  <p className="font-display font-semibold mt-1 line-clamp-2">
                    {stats.next_scheduled.title}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {stats.next_scheduled.scheduled_date}
                  </p>
                </div>
                <Link to={`/scripts/${stats.next_scheduled.day_number}`}>
                  <Button variant="outline" className="w-full" size="sm" data-testid="view-next-scheduled">
                    View Script
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming videos</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generating Status */}
        {stats?.generating > 0 && (
          <Card className="card-patriarch md:col-span-4 lg:col-span-3 border-primary/30" data-testid="generating-status-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                </div>
                <div>
                  <p className="font-medium">{stats.generating} Generating</p>
                  <p className="text-sm text-muted-foreground">Videos in progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
